import React, {
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Input,
  List,
  message,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";

import {
  CopyOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  SwapOutlined,
} from "@ant-design/icons";

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
} from "firebase/firestore";

import { db } from "./FirebaseConfig";

const { Text } = Typography;

/* =========================================================
   HELPERS
========================================================= */

function safeString(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return "";
  }

  return String(value);
}

function toDisplayDate(value) {
  if (!value) return "-";

  if (
    typeof value?.toDate ===
    "function"
  ) {
    try {
      return value
        .toDate()
        .toLocaleString();
    } catch {
      return "-";
    }
  }

  if (
    value instanceof Date
  ) {
    return value.toLocaleString();
  }

  return safeString(value);
}

function normalizeDocument(
  id,
  data = {}
) {
  return {
    key: id,
    id,

    date:
      data.date ??
      data.timestamp ??
      "",

    cultiverName:
      data.cultiverName ??
      "",

    location:
      data.location ??
      "",

    freeText:
      data.freeText ??
      "",

    photopath:
      data.photopath ??
      "",

    size:
      data.size ??
      "",

    temperature:
      data.temperature ??
      data.temperture ??
      "",

    humidity:
      data.humidity ??
      "",

    raw:
      data,
  };
}

/* =========================================================
   MAIN
========================================================= */

const CollectionExplorer = () => {
  /* -------------------------
     collection
  ------------------------- */

  const [
    fromCollection,
    setFromCollection,
  ] = useState("");

  const [
    toCollection,
    setToCollection,
  ] = useState("");

  /* -------------------------
     docs
  ------------------------- */

  const [
    documents,
    setDocuments,
  ] = useState([]);

  const [
    selectedRowKeys,
    setSelectedRowKeys,
  ] = useState([]);

  const [
    selectedDocDetail,
    setSelectedDocDetail,
  ] = useState(null);

  /* -------------------------
     filters
  ------------------------- */

  const [
    searchKeyword,
    setSearchKeyword,
  ] = useState("");

  /* -------------------------
     operation
  ------------------------- */

  const [
    operationMode,
    setOperationMode,
  ] = useState("copy");

  /*
   * duplicateMode
   *
   * skip      : 同IDがあればスキップ
   * overwrite : 上書き
   * newid     : 新しいIDで保存
   */
  const [
    duplicateMode,
    setDuplicateMode,
  ] = useState("skip");

  /* -------------------------
     modal
  ------------------------- */

  const [
    confirmOpen,
    setConfirmOpen,
  ] = useState(false);

  /* -------------------------
     loading
  ------------------------- */

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    processing,
    setProcessing,
  ] = useState(false);

  const [
    progressCurrent,
    setProgressCurrent,
  ] = useState(0);

  const [
    progressTotal,
    setProgressTotal,
  ] = useState(0);

  const [
    lastResult,
    setLastResult,
  ] = useState(null);

  /* ======================================================
     FETCH
  ====================================================== */

  const fetchDocuments =
    async () => {
      const source =
        fromCollection.trim();

      if (!source) {
        message.warning(
          "移動元コレクション名を入力してください。"
        );

        return;
      }

      setLoading(true);

      setDocuments([]);
      setSelectedRowKeys([]);
      setSelectedDocDetail(null);
      setLastResult(null);

      try {
        const colRef =
          collection(
            db,
            source
          );

        const snapshot =
          await getDocs(
            colRef
          );

        if (
          snapshot.empty
        ) {
          message.info(
            "このコレクションにはドキュメントがありません。"
          );

          return;
        }

        const rows =
          snapshot.docs
            .filter(
              (item) =>
                item.id !==
                "_settings"
            )
            .map(
              (item) =>
                normalizeDocument(
                  item.id,
                  item.data()
                )
            );

        setDocuments(rows);

        message.success(
          `${rows.length}件読み込みました。`
        );
      } catch (error) {
        console.error(error);

        message.error(
          "ドキュメント一覧の取得に失敗しました。"
        );
      } finally {
        setLoading(false);
      }
    };

  /* ======================================================
     DETAIL
  ====================================================== */

  const fetchDocDetail =
    async (docId) => {
      const source =
        fromCollection.trim();

      if (!source) return;

      try {
        const docRef =
          doc(
            db,
            source,
            docId
          );

        const snap =
          await getDoc(
            docRef
          );

        if (
          !snap.exists()
        ) {
          message.error(
            "ドキュメントが見つかりません。"
          );

          return;
        }

        setSelectedDocDetail({
          id:
            snap.id,

          data:
            snap.data(),
        });
      } catch (error) {
        console.error(error);

        message.error(
          "詳細取得に失敗しました。"
        );
      }
    };

  /* ======================================================
     FILTER
  ====================================================== */

  const filteredDocuments =
    useMemo(
      () => {
        const keyword =
          searchKeyword
            .trim()
            .toLowerCase();

        if (!keyword) {
          return documents;
        }

        return documents.filter(
          (item) => {
            const target = [
              item.id,
              item.date,
              item.cultiverName,
              item.location,
              item.freeText,
              item.photopath,
            ]
              .map(
                safeString
              )
              .join(" ")
              .toLowerCase();

            return target.includes(
              keyword
            );
          }
        );
      },
      [
        documents,
        searchKeyword,
      ]
    );

  /* ======================================================
     SELECTED
  ====================================================== */

  const selectedDocuments =
    useMemo(
      () =>
        documents.filter(
          (item) =>
            selectedRowKeys.includes(
              item.key
            )
        ),
      [
        documents,
        selectedRowKeys,
      ]
    );

  /* ======================================================
     UNIQUE ID
  ====================================================== */

  const createUniqueDocId =
    (
      baseId,
      index
    ) => {
      const suffix =
        Date.now();

      return `${baseId}_copy_${suffix}_${index + 1}`;
    };

  /* ======================================================
     EXECUTE
  ====================================================== */

  const executeOperation =
    async () => {
      const source =
        fromCollection.trim();

      const destination =
        toCollection.trim();

      if (
        !source ||
        !destination
      ) {
        message.warning(
          "移動元と移動先のコレクションを入力してください。"
        );

        return;
      }

      if (
        source ===
        destination
      ) {
        message.warning(
          "同じコレクションは指定できません。"
        );

        return;
      }

      if (
        !selectedDocuments.length
      ) {
        message.warning(
          "対象ドキュメントを選択してください。"
        );

        return;
      }

      setConfirmOpen(false);
      setProcessing(true);

      setProgressCurrent(0);
      setProgressTotal(
        selectedDocuments.length
      );

      let successCount = 0;
      let skippedCount = 0;
      let failedCount = 0;

      const errors = [];

      try {
        for (
          let i = 0;
          i <
          selectedDocuments.length;
          i++
        ) {
          const item =
            selectedDocuments[i];

          try {
            const sourceRef =
              doc(
                db,
                source,
                item.id
              );

            /*
             * 念のため最新データを取得
             */
            const sourceSnap =
              await getDoc(
                sourceRef
              );

            if (
              !sourceSnap.exists()
            ) {
              failedCount++;

              errors.push({
                id:
                  item.id,

                message:
                  "Source document not found",
              });

              setProgressCurrent(
                i + 1
              );

              continue;
            }

            const latestData =
              sourceSnap.data();

            let destinationId =
              item.id;

            let destinationRef =
              doc(
                db,
                destination,
                destinationId
              );

            const destinationSnap =
              await getDoc(
                destinationRef
              );

            /* ---------------------
               DUPLICATE
            --------------------- */

            if (
              destinationSnap.exists()
            ) {
              if (
                duplicateMode ===
                "skip"
              ) {
                skippedCount++;

                setProgressCurrent(
                  i + 1
                );

                continue;
              }

              if (
                duplicateMode ===
                "newid"
              ) {
                destinationId =
                  createUniqueDocId(
                    item.id,
                    i
                  );

                destinationRef =
                  doc(
                    db,
                    destination,
                    destinationId
                  );
              }

              /*
               * overwriteの場合は
               * destinationRefそのまま
               */
            }

            /* ---------------------
               COPY
            --------------------- */

            await setDoc(
              destinationRef,
              latestData
            );

            /*
             * 保存成功確認
             */
            const verifySnap =
              await getDoc(
                destinationRef
              );

            if (
              !verifySnap.exists()
            ) {
              throw new Error(
                "Destination verification failed"
              );
            }

            /* ---------------------
               MOVE
            --------------------- */

            if (
              operationMode ===
              "move"
            ) {
              /*
               * 移動先保存成功後にのみ
               * 元データを削除
               */
              await deleteDoc(
                sourceRef
              );
            }

            successCount++;
          } catch (error) {
            console.error(
              "Document operation failed:",
              item.id,
              error
            );

            failedCount++;

            errors.push({
              id:
                item.id,

              message:
                error?.message ||
                "Unknown error",
            });
          } finally {
            setProgressCurrent(
              i + 1
            );
          }
        }

        const result = {
          successCount,
          skippedCount,
          failedCount,
          errors,
        };

        setLastResult(
          result
        );

        if (
          failedCount === 0
        ) {
          message.success(
            operationMode ===
            "copy"
              ? `${successCount}件コピーしました。`
              : `${successCount}件移動しました。`
          );
        } else {
          message.warning(
            `完了：成功 ${successCount}件 / スキップ ${skippedCount}件 / 失敗 ${failedCount}件`
          );
        }

        /*
         * 移動の場合は
         * 左側一覧を更新
         */
        if (
          operationMode ===
          "move"
        ) {
          await fetchDocuments();
        } else {
          setSelectedRowKeys([]);
        }
      } finally {
        setProcessing(false);
      }
    };

  /* ======================================================
     CONFIRM
  ====================================================== */

  const openConfirm =
    () => {
      const source =
        fromCollection.trim();

      const destination =
        toCollection.trim();

      if (
        !source ||
        !destination
      ) {
        message.warning(
          "移動元と移動先のコレクションを入力してください。"
        );

        return;
      }

      if (
        source ===
        destination
      ) {
        message.warning(
          "同じコレクションは指定できません。"
        );

        return;
      }

      if (
        !selectedRowKeys.length
      ) {
        message.warning(
          "対象ドキュメントを選択してください。"
        );

        return;
      }

      setConfirmOpen(true);
    };

  /* ======================================================
     TABLE
  ====================================================== */

  const columns = [
    {
      title:
        "撮影日時",

      key:
        "date",

      width:
        170,

      render:
        (_, item) =>
          toDisplayDate(
            item.date
          ),
    },

    {
      title:
        "品種",

      dataIndex:
        "cultiverName",

      key:
        "cultiverName",

      width:
        130,

      render:
        (value) =>
          value || "-",
    },

    {
      title:
        "場所",

      dataIndex:
        "location",

      key:
        "location",

      width:
        120,

      render:
        (value) =>
          value || "-",
    },

    {
      title:
        "コメント",

      dataIndex:
        "freeText",

      key:
        "freeText",

      width:
        260,

      render:
        (value) => (
          <div
            style={{
              maxWidth:
                260,

              overflow:
                "hidden",

              whiteSpace:
                "nowrap",

              textOverflow:
                "ellipsis",
            }}
            title={
              value || ""
            }
          >
            {value || "-"}
          </div>
        ),
    },

    {
      title:
        "画像",

      dataIndex:
        "photopath",

      key:
        "photopath",

      width:
        240,

      render:
        (value) => (
          <Space size={6}>
            <FolderOpenOutlined />

            <span>
              {value || "-"}
            </span>
          </Space>
        ),
    },

    {
      title:
        "気孔数",

      dataIndex:
        "size",

      key:
        "size",

      width:
        90,

      render:
        (value) =>
          value !== ""
            ? value
            : "-",
    },

    {
      title:
        "詳細",

      key:
        "detail",

      width:
        90,

      fixed:
        "right",

      render:
        (_, item) => (
          <Button
            size="small"
            onClick={() =>
              fetchDocDetail(
                item.id
              )
            }
          >
            詳細
          </Button>
        ),
    },
  ];

  /* ======================================================
     UI
  ====================================================== */

  return (
    <div
      style={{
        maxWidth:
          1400,

        margin:
          "0 auto",

        padding:
          24,
      }}
    >
      <div
        style={{
          display:
            "flex",

          justifyContent:
            "space-between",

          alignItems:
            "center",

          marginBottom:
            20,

          gap:
            20,
        }}
      >
        <div>
          <h2
            style={{
              margin:
                0,
            }}
          >
            📂 撮影データ管理
          </h2>

          <Text
            type="secondary"
          >
            Firestoreドキュメントを複数選択して、
            コピーまたは移動できます。
          </Text>
        </div>

        <Button
          icon={
            <ReloadOutlined />
          }
          onClick={
            fetchDocuments
          }
          loading={
            loading
          }
        >
          再読み込み
        </Button>
      </div>

      {/* =================================================
          COLLECTIONS
      ================================================= */}

      <Card
        title="1. コピー元・移動元を指定"
        style={{
          marginBottom:
            16,
        }}
      >
        <Row
          gutter={[
            16,
            16,
          ]}
          align="middle"
        >
          <Col
            xs={24}
            md={10}
          >
            <Text strong>
              元コレクション
            </Text>

            <Input
              style={{
                marginTop:
                  6,
              }}
              placeholder="例：user-a@example.com"
              value={
                fromCollection
              }
              onChange={(e) =>
                setFromCollection(
                  e.target.value
                )
              }
              onPressEnter={
                fetchDocuments
              }
            />
          </Col>

          <Col
            xs={24}
            md={4}
            style={{
              textAlign:
                "center",
            }}
          >
            <RightOutlined
              style={{
                marginTop:
                  22,

                fontSize:
                  22,

                color:
                  "#8c8c8c",
              }}
            />
          </Col>

          <Col
            xs={24}
            md={10}
          >
            <Text strong>
              コピー先・移動先
            </Text>

            <Input
              style={{
                marginTop:
                  6,
              }}
              placeholder="例：user-b@example.com"
              value={
                toCollection
              }
              onChange={(e) =>
                setToCollection(
                  e.target.value
                )
              }
            />
          </Col>
        </Row>

        <div
          style={{
            marginTop:
              14,
          }}
        >
          <Button
            type="primary"
            icon={
              <SearchOutlined />
            }
            loading={
              loading
            }
            onClick={
              fetchDocuments
            }
          >
            ドキュメントを読み込む
          </Button>
        </div>
      </Card>

      {/* =================================================
          OPERATION
      ================================================= */}

      <Card
        title="2. 操作方法"
        style={{
          marginBottom:
            16,
        }}
      >
        <Row
          gutter={[
            24,
            16,
          ]}
        >
          <Col
            xs={24}
            lg={12}
          >
            <Text strong>
              コピー / 移動
            </Text>

            <div
              style={{
                marginTop:
                  10,
              }}
            >
              <Radio.Group
                value={
                  operationMode
                }
                onChange={(e) =>
                  setOperationMode(
                    e.target.value
                  )
                }
              >
                <Space
                  direction="vertical"
                >
                  <Radio value="copy">
                    <Space>
                      <CopyOutlined />

                      <strong>
                        コピー
                      </strong>
                    </Space>

                    <div
                      style={{
                        marginLeft:
                          24,

                        color:
                          "#8c8c8c",

                        fontSize:
                          12,
                      }}
                    >
                      元データを残したまま複製します。
                    </div>
                  </Radio>

                  <Radio value="move">
                    <Space>
                      <SwapOutlined />

                      <strong>
                        移動
                      </strong>
                    </Space>

                    <div
                      style={{
                        marginLeft:
                          24,

                        color:
                          "#8c8c8c",

                        fontSize:
                          12,
                      }}
                    >
                      移動先への保存成功後に元データを削除します。
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </div>
          </Col>

          <Col
            xs={24}
            lg={12}
          >
            <Text strong>
              同じドキュメントIDがある場合
            </Text>

            <div
              style={{
                marginTop:
                  10,
              }}
            >
              <Select
                value={
                  duplicateMode
                }
                style={{
                  width:
                    240,
                }}
                onChange={
                  setDuplicateMode
                }
                options={[
                  {
                    value:
                      "skip",

                    label:
                      "スキップ（推奨）",
                  },

                  {
                    value:
                      "overwrite",

                    label:
                      "上書き",
                  },

                  {
                    value:
                      "newid",

                    label:
                      "別IDで保存",
                  },
                ]}
              />

              <div
                style={{
                  marginTop:
                    8,

                  color:
                    "#8c8c8c",

                  fontSize:
                    12,
                }}
              >
                通常は「スキップ」が安全です。
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* =================================================
          SEARCH + TABLE
      ================================================= */}

      <Card
        title="3. 対象データを選択"
        style={{
          marginBottom:
            16,
        }}
      >
        <div
          style={{
            display:
              "flex",

            justifyContent:
              "space-between",

            gap:
              12,

            flexWrap:
              "wrap",

            marginBottom:
              12,
          }}
        >
          <Input
            prefix={
              <SearchOutlined />
            }
            placeholder="撮影日時・品種・場所・コメント・画像名・IDで検索"
            value={
              searchKeyword
            }
            onChange={(e) =>
              setSearchKeyword(
                e.target.value
              )
            }
            allowClear
            style={{
              maxWidth:
                520,
            }}
          />

          <Space wrap>
            <Tag color="blue">
              読込：
              {
                documents.length
              }
              件
            </Tag>

            <Tag color="green">
              表示：
              {
                filteredDocuments.length
              }
              件
            </Tag>

            <Tag color="orange">
              選択：
              {
                selectedRowKeys.length
              }
              件
            </Tag>
          </Space>
        </div>

        <Table
          rowKey="key"
          columns={
            columns
          }
          dataSource={
            filteredDocuments
          }
          loading={
            loading
          }
          rowSelection={{
            selectedRowKeys,

            onChange:
              setSelectedRowKeys,

            preserveSelectedRowKeys:
              true,
          }}
          pagination={{
            defaultPageSize:
              20,

            showSizeChanger:
              true,

            pageSizeOptions: [
              "20",
              "50",
              "100",
              "200",
            ],
          }}
          scroll={{
            x:
              1200,

            y:
              500,
          }}
        />

        <Divider />

        <div
          style={{
            display:
              "flex",

            justifyContent:
              "space-between",

            alignItems:
              "center",

            gap:
              12,

            flexWrap:
              "wrap",
          }}
        >
          <Text>
            <strong>
              {
                selectedRowKeys.length
              }
            </strong>
            件選択中
          </Text>

          <Button
            type="primary"
            danger={
              operationMode ===
              "move"
            }
            size="large"
            icon={
              operationMode ===
              "copy"
                ? <CopyOutlined />
                : <SwapOutlined />
            }
            disabled={
              !selectedRowKeys.length
            }
            onClick={
              openConfirm
            }
          >
            {operationMode ===
            "copy"
              ? `選択した${selectedRowKeys.length}件をコピー`
              : `選択した${selectedRowKeys.length}件を移動`}
          </Button>
        </div>
      </Card>

      {/* =================================================
          PROGRESS
      ================================================= */}

      {processing && (
        <Card
          title="処理中"
          style={{
            marginBottom:
              16,
          }}
        >
          <Progress
            percent={
              progressTotal
                ? Math.round(
                    (
                      progressCurrent /
                      progressTotal
                    ) *
                      100
                  )
                : 0
            }
          />

          <Text
            type="secondary"
          >
            {progressCurrent}
            {" / "}
            {progressTotal}
            件
          </Text>
        </Card>
      )}

      {/* =================================================
          RESULT
      ================================================= */}

      {lastResult && (
        <Card
          title="処理結果"
          style={{
            marginBottom:
              16,
          }}
        >
          <Row
            gutter={[
              16,
              16,
            ]}
          >
            <Col
              xs={8}
            >
              <Statistic
                title="成功"
                value={
                  lastResult.successCount
                }
              />
            </Col>

            <Col
              xs={8}
            >
              <Statistic
                title="スキップ"
                value={
                  lastResult.skippedCount
                }
              />
            </Col>

            <Col
              xs={8}
            >
              <Statistic
                title="失敗"
                value={
                  lastResult.failedCount
                }
              />
            </Col>
          </Row>

          {lastResult.errors.length >
            0 && (
            <>
              <Divider />

              <Alert
                type="warning"
                showIcon
                message="一部のデータでエラーが発生しました。"
              />

              <List
                size="small"
                dataSource={
                  lastResult.errors
                }
                renderItem={(item) => (
                  <List.Item>
                    <Text code>
                      {
                        item.id
                      }
                    </Text>

                    <Text type="danger">
                      {
                        item.message
                      }
                    </Text>
                  </List.Item>
                )}
              />
            </>
          )}
        </Card>
      )}

      {/* =================================================
          DETAIL
      ================================================= */}

      {selectedDocDetail && (
        <Card
          title={`ドキュメント詳細：${selectedDocDetail.id}`}
          style={{
            marginBottom:
              16,
          }}
          extra={
            <Button
              onClick={() =>
                setSelectedDocDetail(
                  null
                )
              }
            >
              閉じる
            </Button>
          }
        >
          <Descriptions
            size="small"
            bordered
            column={1}
          >
            {Object.entries(
              selectedDocDetail.data
            ).map(
              ([
                key,
                value,
              ]) => (
                <Descriptions.Item
                  label={key}
                  key={key}
                >
                  {typeof value ===
                  "object"
                    ? (
                        <pre
                          style={{
                            whiteSpace:
                              "pre-wrap",

                            margin:
                              0,
                          }}
                        >
                          {JSON.stringify(
                            value,
                            null,
                            2
                          )}
                        </pre>
                      )
                    : String(
                        value
                      )}
                </Descriptions.Item>
              )
            )}
          </Descriptions>
        </Card>
      )}

      {/* =================================================
          CONFIRM MODAL
      ================================================= */}

      <Modal
        open={
          confirmOpen
        }
        onCancel={() =>
          setConfirmOpen(
            false
          )
        }
        onOk={
          executeOperation
        }
        okText={
          operationMode ===
          "copy"
            ? "コピーする"
            : "移動する"
        }
        cancelText="キャンセル"
        okButtonProps={{
          danger:
            operationMode ===
            "move",
        }}
        title={
          operationMode ===
          "copy"
            ? "データをコピーしますか？"
            : "データを移動しますか？"
        }
      >
        <Descriptions
          size="small"
          column={1}
          bordered
        >
          <Descriptions.Item label="元コレクション">
            {
              fromCollection
            }
          </Descriptions.Item>

          <Descriptions.Item label="先コレクション">
            {
              toCollection
            }
          </Descriptions.Item>

          <Descriptions.Item label="対象件数">
            {
              selectedRowKeys.length
            }
            件
          </Descriptions.Item>

          <Descriptions.Item label="操作">
            {operationMode ===
            "copy"
              ? "コピー"
              : "移動"}
          </Descriptions.Item>

          <Descriptions.Item label="重複時">
            {duplicateMode ===
            "skip"
              ? "スキップ"
              : duplicateMode ===
                  "overwrite"
                ? "上書き"
                : "別IDで保存"}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {operationMode ===
        "copy" ? (
          <Alert
            type="info"
            showIcon
            message="元データは削除されません。"
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            message="移動先への保存を確認した後、元Firestoreドキュメントを削除します。"
          />
        )}
      </Modal>
    </div>
  );
};

export default CollectionExplorer;